import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Empty, Form, Input, Popconfirm, Select, Table, Tabs } from 'antd'
import { CalendarOutlined, CaretDownOutlined, CloseCircleOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import i18next from 'i18next'
import { debounce, map } from 'lodash'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'
import { getFormInitialValues } from 'redux-form'
import { NumberParam, StringParam, useQueryParam, useQueryParams, withDefault } from 'use-query-params'
// eslint-disable-next-line import/no-extraneous-dependencies
import { ArrayParam } from 'serialize-query-params/lib/params'
import { deleteFinish, FinishTableItem, getFinishes } from '../../reducers/finishes/actions'
import { getJobs } from '../../reducers/jobs/actions'
import { FREQUENCY, translateFrequencyToSk } from '../../utils/translate'
import { RootState } from '../../reducers'
import { FORM, PAGINATION } from '../../utils/enums'
import SelectEmployeeField from '../../components/SelectEmployeeField'
import DateRangePresetPicker, { DateRangeValue } from '../managerTasks/DateRangePresetPicker'
import FinishesOrders from './FinishesOrders'
// eslint-disable-next-line import/no-extraneous-dependencies

const { Option } = Select

type Columns = ColumnsType<FinishTableItem>

const Finishes = () => {
	const dispatch = useDispatch()
	const { t } = useTranslation('pages')

	const finishes = useSelector((state:RootState) => state.finishes.finishes)
	const isLoadingFinishes = finishes.isLoading

	const filterInitialValues = useSelector((state: RootState) => getFormInitialValues(FORM.FINISHES_FILTER)(state))

	const [query, setQuery] = useQueryParams({
		search: StringParam,
		frequencies: withDefault(ArrayParam, []),
		limit: NumberParam,
		userID: withDefault(NumberParam, undefined),
		jobID: withDefault(NumberParam, undefined),
		page: withDefault(NumberParam, 1),
		orderBy: withDefault(StringParam, 'realDateOfExecution'),
		orderDirection: withDefault(StringParam, 'desc'),
		startDate: StringParam,
		endDate: StringParam
	})

	const dateRange: DateRangeValue | null = query.startDate && query.endDate
		? [moment(query.startDate), moment(query.endDate)]
		: null

	const jobs = useSelector((state: RootState) => state.jobs.jobs.tableData)

	const resetFilter = () => {
		setQuery({
			...query,
			search: '',
			frequencies: [],
			limit: PAGINATION.defaultPageSize,
			userID: undefined,
			jobID: undefined,
			page: 1,
			orderBy: 'realDateOfExecution',
			orderDirection: 'desc',
			startDate: null,
			endDate: null
		})
	}

	useEffect(() => {
		dispatch(getJobs({ limit: 10000, page: 1, queryParams: {} }))
	}, [dispatch])

	useEffect(() => {
		dispatch(getFinishes({
			limit: PAGINATION.defaultPageSize,
			...query
		}))
	}, [dispatch, query])

	const columns: Columns = [
		{
			title: i18next.t('pages:Zákazka'),
			dataIndex: 'address',
			key: 'address',
			render: (address) => address || '-'
		},
		{
			title: i18next.t('pages:Vykonal'),
			dataIndex: 'userName',
			key: 'user',
			render: (userName) => userName
		},
		{
			title: i18next.t('pages:Čas ukončenia'),
			dataIndex: 'realDateOfExecution',
			key: 'realDateOfExecution',
			sorter: true,
			ellipsis: true,
			render(realDateOfExecution) {
				return <span>{realDateOfExecution}</span>
			}
		},
		{
			title: i18next.t('pages:Frekvencia'),
			dataIndex: 'frequency',
			key: 'frequency',
			sorter: true,
			ellipsis: true,
			render: (frequency) => frequency
		},
		{
			title: i18next.t('pages:Hodnotenie'),
			dataIndex: 'rating',
			key: 'rating',
			ellipsis: true,
			render: (value) => (value || '-')
		},
		{
			title: '',
			key: 'operation',
			fixed: 'right',
			width: 50,
			render(text, record) {
				return (
					<Popconfirm
						title={i18next.t('pages:Skutočne chcete vymazať záznam?')}
						icon={<QuestionCircleOutlined style={{ color: 'red' }}/>}
						cancelText={i18next.t('pages:Zrušiť')}
						okText={i18next.t('pages:Vymazať')}
						onConfirm={(e) => {
							e?.stopPropagation()
							dispatch(deleteFinish(record.id, () => {
								dispatch(getFinishes({
									limit: PAGINATION.defaultPageSize,
									page: 1
								}))
							}))
						}}
						onCancel={(e) => e?.stopPropagation()}
						okButtonProps={{
							size: 'small',
							type: 'primary'
						}}
						cancelButtonProps={{
							size: 'small',
							type: 'ghost'
						}}
					>
						<Button
							icon={<DeleteOutlined/>}
							type={'primary'}
							onClick={(e) => e.stopPropagation()}
						/>
					</Popconfirm>
				)
			}
		}
	]

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string,
			FilterValue | null>,
		sorter: SorterResult<FinishTableItem> | SorterResult<FinishTableItem>[],
		extra: TableCurrentDataSource<FinishTableItem>
	) => {
		let order = {}
		if ((sorter as SorterResult<any>)?.order) {
			const sorterLocal = (sorter as SorterResult<any>)
			order = {
				orderBy: sorterLocal.field,
				orderDirection: sorterLocal.order === 'ascend' ? 'asc' : 'desc'
			}
		}

		setQuery({
			...query,
			...order,
			page: pagination.current
		})
	}

	const debounced = useCallback(debounce((searchTerm: string) => setQuery({ ...query, search: searchTerm }), 300), [query])

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		debounced(e.target.value)
	}

	const frequencies = [FREQUENCY.MONDAY, FREQUENCY.TUESDAY, FREQUENCY.WEDNESDAY, FREQUENCY.THURSDAY, FREQUENCY.FRIDAY, FREQUENCY.MONTH, FREQUENCY.QUARTERYEAR, FREQUENCY.HALFYEAR, FREQUENCY.YEAR]

	const [textTemplateParam, setTextTemplateParam] = useQueryParam('textTemplate', NumberParam)

	const onChangeTextTemplate = useCallback((templateID) => {
		setTextTemplateParam(templateID)
	}, [setTextTemplateParam])

	const content = (
		<div className={'page-wrapper'}>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
				<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', flex: 1 }}>
					<Select
						className={'dropDown'}
						style={{ minWidth: 240 }}
						allowClear
						showSearch
						value={query.jobID}
						placeholder={'Vyber zákazku'}
						onChange={(value: any) => setQuery({ ...query, jobID: value ?? undefined, page: 1 })}
						filterOption={(input: any, option: any) => String(option?.children ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
					>
						{map(jobs, (job) => (
							<Option key={job.id} value={job.id}>
								{job.address}
							</Option>
						))}
					</Select>

					<div style={{ marginBottom: -24 }}>
						<SelectEmployeeField
							onValueChange={(data) => setQuery({ ...query, userID: data.value })}
							userValue={query.userID}
						/>
					</div>

					<Select
						style={{ minWidth: 200 }}
						allowClear
						onChange={(value: any) => setQuery({ ...query, frequencies: value ?? [], page: 1 })}
						onClear={() => setQuery({ ...query, frequencies: [], page: 1 })}
						placeholder={t('pages:Frekvencia')}
						value={query.frequencies as any}
						mode={'multiple'}
						filterOption={(input:any, option:any) => String(option?.children ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
					>
						{map(frequencies, (frequency, index) => (
							<Option
								key={`frequency-filter-${index}`}
								value={frequency.toString()}
							>
								{translateFrequencyToSk(frequency)}
							</Option>
						))}
					</Select>

					{dateRange === null ? (
						<button
							type="button"
							onClick={() => {
								const def: DateRangeValue = [moment().subtract(27, 'days').startOf('day'), moment().endOf('day')]
								setQuery({ ...query, startDate: def[0].toISOString(), endDate: def[1].toISOString() })
							}}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 6,
								padding: '0 12px',
								height: 32,
								borderRadius: 6,
								border: '1px solid #d9d9d9',
								background: '#fff',
								fontSize: 13,
								cursor: 'pointer',
								color: '#374151'
							}}
						>
							<CalendarOutlined style={{ fontSize: 13, color: '#9ca3af' }} />
							<span>Dátum</span>
							<CaretDownOutlined style={{ fontSize: 10, color: '#9ca3af' }} />
						</button>
					) : (
						<DateRangePresetPicker
							value={dateRange}
							onChange={(range) => setQuery({ ...query, startDate: range[0].toISOString(), endDate: range[1].toISOString(), page: 1 })}
							onClear={() => setQuery({ ...query, startDate: null, endDate: null, page: 1 })}
						/>
					)}
				</div>

				<Button
					icon={<CloseCircleOutlined/>}
					onClick={resetFilter}
					style={{ flexShrink: 0, marginLeft: 16 }}
				>
					{t('pages:Zrušiť filter')}
				</Button>
			</div>
			<Table
				className={'general-table'}
				columns={columns}
				dataSource={finishes.tableData}
				onChange={handleTableChange}
				showSorterTooltip={false}
				rowKey={'id'}
				style={{ marginTop: -20 }}
				pagination={{
					pageSize: PAGINATION.defaultPageSize,
					total: finishes.pagination?.totalCount,
					current: finishes.pagination?.page,
					showSizeChanger: false
				}}
				loading={isLoadingFinishes}
				onRow={(record) => ({
					// onClick: () => history.push(`${ t('paths:user.path') }/${ get(record, 'id') }`)
				})}
				locale={{
					emptyText: (
						<Empty description={t('pages:Žiadne dáta')}/>
					)
				}}
				size={'small'}
			/>
		</div>
	)

	return (
		<Tabs defaultActiveKey="all" size={'large'}>
			<Tabs.TabPane tab={t('pages:Všetky')} key="all">
				{content}
			</Tabs.TabPane>
			<Tabs.TabPane tab={t('pages:Objednávky')} key="orders">
				<FinishesOrders />
			</Tabs.TabPane>
		</Tabs>
	)
}

Finishes.propTypes = {}

export default Finishes
